import { Link } from 'react-router-dom';
import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import { ProductionSection } from '../../prodstats/dashboard/ProductionSection';
import { useProductionSummary } from '../../prodstats/dashboard/summary';
import { RefreshCw } from 'lucide-react';
import { Button, Chip } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Page, TableScroller, useAsync } from '../../components/common';
import { stats, type IngestHealth, type Regression } from '../../api/stats';
import { formatDateTime, formatDuration, formatRelative } from '../../lib/format';
import { IssueList, Kpi, joinParts } from '../dashboard/shared';
import { LAG_WARN_SECONDS, silentSince, statsIssues, useStatsSummary } from '../dashboard/stats';
import { Sparkline } from '@nyce/admin-kit/console';
import { IngestStatus, OrgLink, fmtInt, fmtMs, installationPath } from './shared';
import './statistics.css';

function Regressions({ list }: { list: Regression[] }) {
  return (
    <div className="nyk-card" data-testid="regressions">
      <div className="nyk-card-head">
        <h2>Regressions in the last 24 h</h2>
        <span className="nyk-ui-hint">p95 vs the same weekday and hour of the previous 4 weeks</span>
      </div>
      {!list.length ? (
        <EmptyState
          title="No regressions."
          hint="No installation is markedly slower than its 4-week baseline."
        />
      ) : (
        <TableScroller>
          <table className="nyk-ui-table nyk-cards">
            <thead>
              <tr>
                <th>Installation</th>
                <th>Method</th>
                <th className="sx-num">p95 now</th>
                <th className="sx-num">Baseline</th>
                <th className="sx-num">Ratio</th>
                <th>Version change</th>
              </tr>
            </thead>
            <tbody>
              {list.slice(0, 15).map((r) => (
                <tr key={`${r.customerId}:${r.workserver}:${r.method}`}>
                  <td className="nyk-card-title">
                    <Link
                      className="nyk-link"
                      to={installationPath(r.customerId, r.workserver, 'regressions')}
                    >
                      {r.workserver}
                    </Link>
                    <div className="nyk-ui-hint">{r.name ?? `Customer ${r.customerId}`}</div>
                  </td>
                  <td data-label="Method" className="nyk-wrap">
                    {r.method}
                  </td>
                  <td data-label="p95 now" className="sx-num">
                    {fmtMs(r.currentP95)}
                  </td>
                  <td data-label="Baseline" className="sx-num">
                    {fmtMs(r.baselineP95)}
                  </td>
                  <td data-label="Ratio" className="sx-num">
                    ×{r.ratio.toFixed(1)}
                  </td>
                  <td data-label="Version change">
                    {r.versionChange ? `${r.versionChange.from ?? '?'} → ${r.versionChange.to}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      )}
    </div>
  );
}

export function IngestCard({ ingest }: { ingest: IngestHealth }) {
  const lagWarn = ingest.backlog.lagSeconds != null && ingest.backlog.lagSeconds > LAG_WARN_SECONDS;
  return (
    <div className="nyk-card" data-testid="ingest">
      <div className="nyk-card-head">
        <h2>Ingest health</h2>
        <Chip size="sm" className={`oph-status nyk-tone-${ingest.processor.stale ? 'red' : 'green'}`}>
          {ingest.processor.stale ? 'Processor not running' : 'Processor running'}
        </Chip>
      </div>
      <dl className="sx-meta">
        <span>
          Processor state <b>{formatRelative(ingest.processor.updatedAt)}</b>
        </span>
        <span className={lagWarn ? 'nyk-text-amber' : undefined}>
          Backlog <b>{fmtInt(ingest.backlog.rows)}</b> rows
          {ingest.backlog.lagSeconds != null && (
            <> · oldest {formatDuration(ingest.backlog.lagSeconds * 1000)}</>
          )}
        </span>
        <span>
          Dead letters <b>{fmtInt(ingest.failed.rows)}</b> ({ingest.failed.last24h} in 24 h)
        </span>
        <span>
          Rejected 24 h: <b>{fmtInt(ingest.rejected24h.invalidToken)}</b> invalid token ·{' '}
          <b>{fmtInt(ingest.rejected24h.missingToken)}</b> missing ·{' '}
          <b>{fmtInt(ingest.rejected24h.malformed)}</b> malformed
        </span>
      </dl>
      {!!ingest.jobs.length && (
        <TableScroller>
          <table className="nyk-ui-table nyk-cards">
            <thead>
              <tr>
                <th>Job</th>
                <th>Period</th>
                <th>Last run</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {ingest.jobs.map((j) => (
                <tr key={j.job}>
                  <td className="nyk-card-title">{j.job}</td>
                  <td data-label="Period">{j.period}</td>
                  <td data-label="Last run">{formatDateTime(j.startedAt)}</td>
                  <td data-label="Status">
                    {j.status}
                    {j.detail && <div className="nyk-ui-hint">{j.detail}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      )}
    </div>
  );
}

/** Statistics group dashboard (replaces PMS's OpsDashboard): fleet health first, then where to look. */
export function StatisticsDashboardPage({ scopes }: { scopes: readonly string[] }) {
  const canStats = hasScope(scopes, SCOPES.statsRead);
  const res = useStatsSummary(canStats);
  const versions = useAsync(() => (canStats ? stats.versions(14) : Promise.resolve(null)), [canStats]);
  const groups = useAsync(
    () =>
      canStats ? stats.errorGroups({ status: 'active', sort: 'last', limit: 10 }) : Promise.resolve(null),
    [canStats]
  );
  const pr = useProductionSummary();
  if (!canStats)
    return (
      <Page title="Statistics" description="What the nyce.logic databases produce.">
        <ProductionSection pr={pr} />
      </Page>
    );
  const s = res.data;
  const f = s?.fleet;
  return (
    <Page
      title="Statistics"
      description="What the WorkServers report to the datacollector: installations, incidents, errors and the ingest pipeline."
      actions={
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw size={14} />}
          loading={res.loading}
          onClick={() => {
            res.reload();
            void pr.reload();
            void versions.reload();
            void groups.reload();
          }}
        >
          Refresh
        </Button>
      }
    >
      <ErrorBox error={res.error} />
      {s?.errors.map((e) => (
        <ErrorBox key={e} error={e} />
      ))}
      {pr.enabled && <ProductionSection pr={pr} />}
      {!s && res.loading && <div className="oph-dash-skeleton" aria-busy="true" />}
      {s && f && (
        <div className={res.loading ? 'oph-dash-stale' : undefined}>
          <div className="nyk-kpis" data-testid="stats-kpis">
            <Kpi
              label="Organizations reporting"
              value={`${f.organizationsReporting} / ${f.organizationsActive}`}
              sub="with monitoring on"
              to="/statistics/orgs"
            />
            <Kpi
              label="Installations"
              value={`${f.installations.ok} / ${f.installations.ok + f.installations.silent}`}
              sub={joinParts([
                'reporting',
                f.installations.silent > 0 && `${f.installations.silent} silent`,
                f.installations.never > 0 && `${f.installations.never} never`,
              ])}
              tone={f.installations.silent ? 'red' : undefined}
            />
            <Kpi
              label="Open incidents"
              value={f.incidentsOpen}
              sub={`${f.incidentsActive} not closed`}
              tone={f.incidentsOpen ? 'amber' : undefined}
              to="/statistics/incidents"
            />
            <Kpi
              label="Open errors"
              value={fmtInt(f.errorsOpen)}
              sub={`${f.errorGroupsNew24h} new groups in 24 h`}
              to="/statistics/errors"
            />
            <Kpi
              label="Ingest"
              value={s.ingest?.processor.stale ? 'stale' : 'ok'}
              sub={joinParts([`backlog ${fmtInt(f.backlogRows)}`, `${fmtInt(f.rejected24h)} rejected 24 h`])}
              tone={s.ingest?.processor.stale ? 'red' : undefined}
            />
          </div>
          <IssueList
            issues={statsIssues(s)}
            title="Needs attention"
            clear="All installations report; no regressions."
          />
          {!!s.silent.length && (
            <div className="nyk-card" data-testid="silent">
              <div className="nyk-card-head">
                <h2>Silent installations</h2>
                <span className="nyk-ui-hint">{s.silent.length}</span>
              </div>
              <TableScroller>
                <table className="nyk-ui-table nyk-cards">
                  <thead>
                    <tr>
                      <th>Installation</th>
                      <th>Organization</th>
                      <th>Last data</th>
                      <th>Stale signals</th>
                      <th>Version</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.silent.map((i) => (
                      <tr key={`${i.customerId}:${i.workserver}`}>
                        <td className="nyk-card-title">
                          <Link className="nyk-link" to={installationPath(i.customerId, i.workserver)}>
                            {i.workserver}
                          </Link>{' '}
                          <IngestStatus status={i.status} />
                        </td>
                        <td data-label="Organization">
                          <OrgLink customerId={i.customerId} name={i.name} />
                        </td>
                        <td data-label="Last data">{formatRelative(silentSince(i))}</td>
                        <td data-label="Stale signals">{i.silentKinds.join(', ') || '—'}</td>
                        <td data-label="Version">{i.version ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            </div>
          )}
          <Regressions list={s.regressions} />
          <div className="nyk-card" data-testid="error-groups">
            <div className="nyk-card-head">
              <h2>Active error groups</h2>
              <Link to="/statistics/errors" className="nyk-ui-hint">
                All error groups
              </Link>
            </div>
            <ErrorBox error={groups.error} />
            {groups.data && !groups.data.items.length && (
              <EmptyState title="No open errors in the last 7 days." />
            )}
            {!!groups.data?.items.length && (
              <TableScroller>
                <table className="nyk-ui-table nyk-cards">
                  <thead>
                    <tr>
                      <th>Error</th>
                      <th className="sx-num">Open</th>
                      <th>7 days</th>
                      <th>Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.data.items.map((g) => (
                      <tr key={g.fingerprint}>
                        <td className="nyk-card-title sx-msg">
                          <Link
                            className="nyk-link sx-msg-clip"
                            to={`/statistics/errors/${encodeURIComponent(g.fingerprint)}`}
                          >
                            {g.title}
                          </Link>
                          <div className="nyk-ui-hint">
                            {g.organizations} organizations{g.new && ' · new'}
                          </div>
                        </td>
                        <td data-label="Open" className="sx-num">
                          {fmtInt(g.open)}
                        </td>
                        <td data-label="7 days">
                          <Sparkline values={g.trend.map((t) => t.count)} label={`${g.count} in 7 days`} />
                        </td>
                        <td data-label="Last seen">{formatRelative(g.lastSeen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            )}
          </div>
          {s.ingest && <IngestCard ingest={s.ingest} />}
          <div className="nyk-card" data-testid="versions">
            <div className="nyk-card-head">
              <h2>WMS versions</h2>
              <span className="nyk-ui-hint">installations that sent a daily metric in the last 14 days</span>
            </div>
            <ErrorBox error={versions.error} />
            {!!versions.data?.length && (
              <TableScroller>
                <table className="nyk-ui-table nyk-cards">
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th className="sx-num">Installations</th>
                      <th className="sx-num">Organizations</th>
                      <th>Where</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.data.map((v) => (
                      <tr key={`${v.version} ${v.release ?? ''}`}>
                        <td className="nyk-card-title">
                          {v.version}
                          {v.release && <span className="nyk-ui-hint"> {v.release}</span>}
                        </td>
                        <td data-label="Installations" className="sx-num">
                          {v.installations}
                        </td>
                        <td data-label="Organizations" className="sx-num">
                          {v.organizations}
                        </td>
                        <td data-label="Where" className="nyk-wrap">
                          {v.items
                            .slice(0, 4)
                            .map((i) => `${i.name ?? i.customerId} · ${i.workserver}`)
                            .join(', ')}
                          {v.items.length > 4 && ` and ${v.items.length - 4} more`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            )}
          </div>
        </div>
      )}
    </Page>
  );
}
